import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { activities } from '../data/activities';

// Import des images pour éviter les problèmes de chemin
import activity1 from '../assets/images/activity1.jpg';
import activity2 from '../assets/images/activity2.jpg';
import activity3 from '../assets/images/activity3.jpg';

// Mapping des chemins d'images
const imageMap: {[key: string]: string} = {
  '/src/assets/images/activity1.jpg': activity1,
  '/src/assets/images/activity2.jpg': activity2,
  '/src/assets/images/activity3.jpg': activity3
};

const Activities: React.FC = () => {
  const [sectionRef, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="activities" className="py-20 bg-gradient-to-b from-white to-[#F5F5F5]" ref={sectionRef}>
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-4">
            Activités à <span className="text-[#87CEFA]">découvrir</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explorez notre sélection d'activités culturelles et de loisirs pour enrichir l'expérience de vos enfants et stimuler leur créativité.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {activities.map((activity) => (
            <motion.div 
              key={activity.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              variants={cardVariants}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <div className="h-48 overflow-hidden">
                <img 
                  src={imageMap[activity.image]} 
                  alt={activity.title} 
                  className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-bold text-[#1C1C1C]">{activity.title}</h3>
                  <span className="bg-[#87CEFA] text-[#1C1C1C] text-xs font-semibold px-2.5 py-1 rounded-full">
                    {activity.type}
                  </span>
                </div>
                <p className="text-gray-600 mb-4">{activity.description}</p>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-[#FFD700] font-semibold">{activity.ageRange}</span>
                  <span className="text-gray-500">{activity.date}</span>
                </div>
                <motion.button 
                  className="mt-4 w-full bg-[#87CEFA] hover:bg-[#66B2FF] text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  S'inscrire
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <motion.button 
            className="bg-[#1C1C1C] hover:bg-[#333333] text-white font-bold py-3 px-8 rounded-lg shadow-md transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Voir toutes les activités
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Activities;
